import React from 'react';
import printJS from 'print-js';
import PropTypes from 'prop-types';
import {
  DOC_TYPE_IMMEDIATE_CONFIRMATION,
  DOC_TYPE_INFORMATION,
  longestWord
} from '../helpers/utils';
import II18 from '../translate/II18';
import { gae, gaPageView } from '../helpers/Analytics';
import DocumentHelper from '../helpers/DocumentHelper';
import { confirmDialog } from '../helpers/dialogUtils';
import { downloadURI } from '../../lib/fileUtils';
import PaperflyAPI from '../../server/api/PaperflyAPI';
import Spinner from '../helpers/Spinner';

export default function withViewer(WrappedComponent) {
  // noinspection JSPotentiallyInvalidUsageOfThis
  class WithViewerWrapper extends React.Component {
    constructor(props) {
      super(props);
      this.state = {
        documentIsScrolledTillEnd: props.document.attachment.length === 1,
        currentPage: 0,
        showActions: false,
        zoomed: -1
      };
      this.pagesSlider = null;
      this.editBar = null;
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
      if (
        !prevState.documentIsScrolledTillEnd &&
        this.state.documentIsScrolledTillEnd
      ) {
        this.pushDocumentStateUp();
      }

      if (
        this.props.documentStatus?.downloaded &&
        !prevProps.documentStatus?.downloaded
      ) {
        this.setInfoDocumentRead();
      }
    }

    pushDocumentStateUp = () => {
      this.props.shareActiveDocumentState({
        currentPageIsLast: this.state.documentIsScrolledTillEnd
      });
    };

    updateDocumentStatus = status => {
      this.props.setDocumentStatus(this.props.document.id, {
        ...this.props.documentStatus,
        ...status
      });
    };

    componentDidMount() {
      this._isMounted = true;
      gaPageView('Viewer');

      if (!this.props.scanEnded) {
        if (this.props.downloadRequired) {
          this.onShowDownloadRequiredDialog();
        } else this.setInfoDocumentRead();
      }
      if (this.state.documentIsScrolledTillEnd) {
        this.pushDocumentStateUp();
      }
    }

    componentWillUnmount() {
      this._isMounted = false;
      clearTimeout(this.infoDocumentDoneTimeout);
    }

    setInfoDocumentRead = () => {
      if (this.props.document.type === DOC_TYPE_INFORMATION) {
        gae('VIEWER', 'SET_INFO_DOCUMENT_STATUS.READ');
        this.updateDocumentStatus({ read: true });

        if (
          this.props.isSkipDocumentListMode &&
          !this.props.templateMode &&
          !this.props.manyPagesRequestNextButton
        ) {
          // Defer auto-complete until after F7 has finished navigating to
          // Viewer; sending in the same tick races page mount and can leave
          // the user on Home (ViewerContent scroll bind also needs the page).
          gae('VIEWER', 'SET_INFO_DOCUMENT_DONE');
          clearTimeout(this.infoDocumentDoneTimeout);
          this.infoDocumentDoneTimeout = setTimeout(() => {
            if (!this._isMounted) {
              return;
            }
            this.props.onDocumentDone(true);
          }, 300);
        }
      }
    };

    errorOnGetDownloadRequiredUrl = () => {
      this.setState({
        waitingForProcessing: false
      });
      this.onShowDownloadRequiredDialog(true);
    };

    downloadRequiredDocument = () => {
      this.setState({ waitingForProcessing: true });
      PaperflyAPI.getDownloadRequiredUrl(
        this.props.recipientRequestId,
        this.props.document.id
      ).then(
        response => {
          if (response.downloadUrl) {
            downloadURI(
              response.downloadUrl,
              this.props.document.name + '.pdf'
            ).then(() => {
              this.setState({
                waitingForProcessing: false
              });
              this.updateDocumentStatus({ downloaded: true });
            });
          } else {
            this.errorOnGetDownloadRequiredUrl();
          }
        },
        () => this.errorOnGetDownloadRequiredUrl()
      );
    };

    onShowDownloadRequiredDialog = errorOccurred => {
      const thiss = this;
      confirmDialog(
        II18.get('viewer_download_required_title'),
        II18.get(
          errorOccurred ? 'technical_error' : 'viewer_download_required_message'
        ),
        () => {
          gae('VIEWER', 'START_DOWNLOAD_DOCUMENT');
          thiss.downloadRequiredDocument();
        },
        this.props.navigateBack,
        II18.get('title_download'),
        this.props.documentCanNavigateBack ? II18.get('title_cancel') : '',
        !window.isDesktop
      );
    };

    onShowConfirmationDialog = cancelConfirmation => {
      if (
        !cancelConfirmation &&
        (this.props.confirmed || this.props.document?.rejected)
      ) {
        this.props.onDocumentDone();
      } else {
        const thiss = this;
        const hasLongestWord =
          longestWord(this.getConfirmText()).length > 25 ||
          this.getConfirmText().length > 200;

        confirmDialog(
          cancelConfirmation
            ? II18.get('title_unconfirm')
            : this.props.document.attributes.actionButtonLabel ||
                II18.get('title_confirm'),
          '',
          () => {
            gae('VIEWER', 'SET_DOCUMENT_CONFIRM_STATUS', !cancelConfirmation);
            thiss.updateDocumentStatus({
              confirmed: !cancelConfirmation
            });
            !cancelConfirmation && thiss.props.onDocumentDone(true);
          },
          null,
          II18.get('common_yes'),
          II18.get(cancelConfirmation ? 'title_no' : 'title_cancel'),
          !window.isDesktop,
          hasLongestWord ? 'dialogLongestText' : ''
        );
      }
    };

    doPrintAttachment = () => {
      printJS({
        maxWidth: 2000,
        documentTitle: ' ',
        printable: this.props.document.attachment,
        type: 'image',
        imageStyle: 'max-width: 99%;max-height: 99%;object-fit: contain;'
      });
    };

    doDownloadAttachment = () => {
      if (this.props.document.attributes.signedSourcePdfUrl) {
        downloadURI(
          this.props.document.attributes.signedSourcePdfUrl,
          this.props.document.name + '.pdf'
        );
      } else {
        const docHelper = new DocumentHelper(
          this.props.document.attachment.map(attach => ({
            url: attach
          })),
          this.props.document.name
        );
        //docHelper.convertPagesToDataUrls(() => docHelper.download());
        docHelper.download();
      }
    };

    printAttachment = () => {
      this.doPrintAttachment();
    };

    getConfirmText() {
      return this.props.confirmed
        ? II18.get('documents_confirmed')
        : this.props.document.attributes.actionButtonLabel
        ? this.props.document.attributes.actionButtonLabel
        : II18.get('title_confirm');
    }

    render() {
      const {
        document,
        scanEnded,
        isSkipDocumentListMode,
        templateMode,
        confirmed,
        manyPagesRequestNextButton,
        downloadRequired
      } = this.props;

      const docType = document.type;

      const showScrollButton =
        document.attachment.length > 1 &&
        (document.attributes.scrollToEndOptionEnabled || 1) &&
        !this.state.documentIsScrolledTillEnd &&
        !downloadRequired &&
        !confirmed &&
        !scanEnded;

      const showConfirmButton =
        !showScrollButton &&
        docType === DOC_TYPE_IMMEDIATE_CONFIRMATION &&
        !confirmed &&
        !scanEnded;

      const showUnConfirmButton =
        docType === DOC_TYPE_IMMEDIATE_CONFIRMATION && confirmed && !scanEnded;

      const showAcceptDoneButton =
        docType === DOC_TYPE_IMMEDIATE_CONFIRMATION &&
        confirmed &&
        isSkipDocumentListMode &&
        templateMode &&
        !manyPagesRequestNextButton &&
        !scanEnded;

      const confirmText = this.getConfirmText();
      return (
        <>
          <WrappedComponent
            onDocumentIsScrolledTillEnd={() =>
              this.setState({ documentIsScrolledTillEnd: true })
            }
            onShowDownloadRequiredDialog={() =>
              this.onShowDownloadRequiredDialog()
            }
            onShowConfirmationDialog={() => this.onShowConfirmationDialog()}
            onShowUnConfirmationDialog={() =>
              this.onShowConfirmationDialog(true)
            }
            documentIsScrolledTillEnd={this.state.documentIsScrolledTillEnd}
            showAcceptDoneButton={showAcceptDoneButton}
            showUnConfirmButton={showUnConfirmButton}
            showConfirmButton={showConfirmButton}
            showScrollButton={showScrollButton}
            confirmText={confirmText}
            printAttachment={this.printAttachment}
            doDownloadAttachment={this.doDownloadAttachment}
            {...this.props}
          />
          {this.state.waitingForProcessing && <Spinner fullScreen />}
        </>
      );
    }
  }

  WithViewerWrapper.propTypes = {
    downloadRequired: PropTypes.bool,
    recipientRequestId: PropTypes.string,
    navigateBack: PropTypes.func,
    setDocumentStatus: PropTypes.func,
    documentStatus: PropTypes.object,
    confirmed: PropTypes.bool,
    downloadRequiredDocument: PropTypes.func,

    //withDocumentCommonPropTypes
    documentCanNavigateBack: PropTypes.bool,
    isSkipDocumentListMode: PropTypes.bool,
    templateMode: PropTypes.bool,
    manyPagesRequestNextButton: PropTypes.bool,
    onDocumentDone: PropTypes.func,
    scanEnded: PropTypes.bool,
    document: PropTypes.object,
    shareActiveDocumentState: PropTypes.func
  };

  return WithViewerWrapper;
}
